import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';
import { debugLogger } from '@/utils/debugLogger';

interface RealtimeSubscriber {
  id: string;
  onMessage?: (message: any) => void;
  onNotification?: (notification: any) => void;
  onNotificationDeleted?: (notification: any) => void;
  onPresenceUpdate?: (presence: any) => void;
  onConnectionChanged?: (connection: any) => void;
  onConversationUpdate?: (conversation: any) => void;
  onProfileUpdate?: (profile: any) => void;
}

interface ChatPresenceData {
  userId: string;
  status: 'online' | 'typing' | 'recording audio';
  timestamp: number;
}

// FIXED: Structure for presence data that matches what useSimplePresence expects
interface PresenceUser {
  userId: string;
  status: string;
  lastUpdate: number;
}

class RealtimeCentral {
  private static instance: RealtimeCentral;
  private subscribers: Map<string, RealtimeSubscriber> = new Map();
  private chatChannels: Map<string, RealtimeChannel> = new Map();
  private notificationChannel: RealtimeChannel | null = null;
  private presenceChannel: RealtimeChannel | null = null;
  private connectionsChannel: RealtimeChannel | null = null;
  private conversationsChannel: RealtimeChannel | null = null;
  private profileChannel: RealtimeChannel | null = null;
  private currentUserId: string | null = null;
  private isActivated: boolean = false;
  
  private constructor() {}
  
  static getInstance(): RealtimeCentral {
    if (!RealtimeCentral.instance) {
      RealtimeCentral.instance = new RealtimeCentral();
    }
    return RealtimeCentral.instance;
  }
  
  // SIMPLIFIED INITIALIZATION - NO COMPLEX RETRY LOGIC
  initialize(userId: string) {
    if (this.currentUserId === userId && this.isActivated) {
    debugLogger.debug('RealtimeCentral', 'Already initialized for user:', userId);
      return;
    }

    debugLogger.debug('RealtimeCentral', 'Initializing for user:', userId);
    this.currentUserId = userId;
    this.activate();
  }
  
  // SIMPLIFIED ACTIVATION - NO CIRCUIT BREAKER
  activate() {
    if (!this.currentUserId) {
      debugLogger.warn('RealtimeCentral', 'Cannot activate - no user');
      return;
    }
    
    if (this.isActivated) {
      debugLogger.debug('RealtimeCentral', 'Already activated');
      return;
    }
    
    debugLogger.debug('RealtimeCentral', 'Activating realtime system for user:', this.currentUserId);
    this.isActivated = true;
    
    try {
      this.setupNotificationChannel();
      this.setupPresenceChannel();
      this.setupConnectionsChannel();
      this.setupConversationsChannel();
      this.setupProfileChannel();
    } catch (error) {
      debugLogger.error('RealtimeCentral', 'Error during activation:', error);
      this.isActivated = false;
    }
  }
  
  // Check if system is activated
  isActive(): boolean {
    return this.isActivated && this.currentUserId !== null;
  }

  // Simplified debug status
  getDebugStatus(): any {
    return {
      isActivated: this.isActivated,
      currentUserId: this.currentUserId,
      subscribersCount: this.subscribers.size,
      chatChannelsCount: this.chatChannels.size,
      hasNotificationChannel: !!this.notificationChannel,
      hasPresenceChannel: !!this.presenceChannel,
      hasConnectionsChannel: !!this.connectionsChannel,
      hasConversationsChannel: !!this.conversationsChannel,
      hasProfileChannel: !!this.profileChannel
    };
  }

  // SIMPLE RECONNECT - NO COMPLEX LOGIC
  forceReconnect(): void {
    debugLogger.debug('RealtimeCentral', 'Force reconnect requested');
    
    if (this.currentUserId) {
      this.cleanup();
      this.activate();
    }
  }
  
  // Simplified notification channel setup
  private setupNotificationChannel() {
    if (!this.currentUserId || this.notificationChannel) return;
    
    debugLogger.debug('RealtimeCentral', 'Setting up notification channel');
    
    this.notificationChannel = supabase
      .channel(`notifications_${this.currentUserId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${this.currentUserId}`,
        },
        (payload) => {
          debugLogger.debug('RealtimeCentral', 'New notification:', payload.new);
          this.notifySubscribers('notification', payload.new);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${this.currentUserId}`,
        },
        (payload) => {
          debugLogger.debug('RealtimeCentral', 'Notification deleted:', payload.old);
          this.notifySubscribers('notificationDeleted', payload.old);
        }
      )
      .subscribe((status) => {
        debugLogger.debug('RealtimeCentral', 'Notification channel status:', status);
      });
  }
  
  // Simplified connections channel setup
  private setupConnectionsChannel() {
    if (!this.currentUserId || this.connectionsChannel) return;
    
    debugLogger.debug('RealtimeCentral', 'Setting up connections channel');
    
    this.connectionsChannel = supabase
      .channel(`connections_${this.currentUserId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'connections',
          filter: `user_id=eq.${this.currentUserId}`,
        },
        (payload) => {
          debugLogger.debug('RealtimeCentral', 'Connection change (as sender):', payload);
          this.notifySubscribers('connectionChanged', payload);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'connections',
          filter: `target_id=eq.${this.currentUserId}`,
        },
        (payload) => {
          debugLogger.debug('RealtimeCentral', 'Connection change (as target):', payload);
          this.notifySubscribers('connectionChanged', payload);
        }
      )
      .subscribe((status) => {
        debugLogger.debug('RealtimeCentral', 'Connections channel status:', status);
      });
  }
  
  // Simplified conversations channel setup
  private setupConversationsChannel() {
    if (!this.currentUserId || this.conversationsChannel) return;
    
    debugLogger.debug('RealtimeCentral', 'Setting up conversations channel');
    
    this.conversationsChannel = supabase
      .channel(`conversations_${this.currentUserId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
          filter: `user1_id=eq.${this.currentUserId}`,
        },
        (payload) => {
          debugLogger.debug('RealtimeCentral', 'Conversation update (as user1):', payload);
          this.notifySubscribers('conversationUpdate', payload);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
          filter: `user2_id=eq.${this.currentUserId}`,
        },
        (payload) => {
          debugLogger.debug('RealtimeCentral', 'Conversation update (as user2):', payload);
          this.notifySubscribers('conversationUpdate', payload);
        }
      )
      .subscribe((status) => {
        debugLogger.debug('RealtimeCentral', 'Conversations channel status:', status);
      });
  }

  // Simplified presence channel setup
  private setupPresenceChannel() {
    if (!this.currentUserId || this.presenceChannel) return;
    
    debugLogger.debug('RealtimeCentral', 'Setting up presence channel');
    
    this.presenceChannel = supabase
      .channel(`presence_${this.currentUserId}`)
      .on('presence', { event: 'sync' }, () => {
        const state = this.presenceChannel?.presenceState();
        debugLogger.debug('RealtimeCentral', 'Presence sync:', state);
        this.notifySubscribers('presenceUpdate', { event: 'sync', state });
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        debugLogger.debug('RealtimeCentral', 'Presence join:', { key, newPresences });
        this.notifySubscribers('presenceUpdate', { event: 'join', key, newPresences });
      })
      .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        debugLogger.debug('RealtimeCentral', 'Presence leave:', { key, leftPresences });
        this.notifySubscribers('presenceUpdate', { event: 'leave', key, leftPresences });
      })
      .subscribe(async (status) => {
        debugLogger.debug('RealtimeCentral', 'Presence channel status:', status);
        if (status === 'SUBSCRIBED') {
          await this.presenceChannel?.track({
            userId: this.currentUserId,
            status: 'online',
            timestamp: Date.now()
          });
        }
      });
  }

  // Simplified profile channel setup
  private setupProfileChannel() {
    if (!this.currentUserId || this.profileChannel) return;
    
    debugLogger.debug('RealtimeCentral', 'Setting up profile channel');
    
    this.profileChannel = supabase
      .channel(`profile_${this.currentUserId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${this.currentUserId}`,
        },
        (payload) => {
          debugLogger.debug('RealtimeCentral', 'Profile updated:', payload.new);
          this.notifySubscribers('profileUpdate', payload.new);
        }
      )
      .subscribe((status) => {
        debugLogger.debug('RealtimeCentral', 'Profile channel status:', status);
      });
  }

  // Subscribe to realtime events
  subscribe(subscriberId: string, subscriber: RealtimeSubscriber): () => void {
    debugLogger.debug('RealtimeCentral', 'Adding subscriber:', subscriberId);
    this.subscribers.set(subscriberId, subscriber);
    
    return () => {
      debugLogger.debug('RealtimeCentral', 'Removing subscriber:', subscriberId);
      this.subscribers.delete(subscriberId);
    };
  }

  // Join chat channel for specific conversation
  joinChatChannel(conversationId: string): void {
    if (this.chatChannels.has(conversationId)) {
      debugLogger.debug('RealtimeCentral', 'Already connected to chat channel:', conversationId);
      return;
    }

    debugLogger.debug('RealtimeCentral', 'Joining chat channel for conversation:', conversationId);
    
    const channel = supabase
      .channel(`chat_${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          debugLogger.debug('RealtimeCentral', 'New message in chat channel:', payload.new);
          this.notifySubscribers('message', {
            message: payload.new,
            conversationId
          });
        }
      )
      .subscribe((status) => {
        debugLogger.debug('RealtimeCentral', `Chat channel for ${conversationId} status:`, status);
      });

    this.chatChannels.set(conversationId, channel);
  }

  // Leave chat channel
  leaveChatChannel(conversationId: string): void {
    const channel = this.chatChannels.get(conversationId);
    if (channel) {
      debugLogger.debug('RealtimeCentral', 'Leaving chat channel:', conversationId);
      supabase.removeChannel(channel);
      this.chatChannels.delete(conversationId);
    }
  }

  // SIMPLIFIED presence update - no throttling
  updateChatPresence(conversationId: string, presenceData: ChatPresenceData): void {
    const channel = this.chatChannels.get(conversationId);
    if (channel) {
      debugLogger.debug('RealtimeCentral', 'Updating chat presence:', { conversationId, presenceData });
      channel.track({
        ...presenceData,
        timestamp: Date.now()
      });
    }
  }

  // Notify all subscribers
  private notifySubscribers(eventType: string, data: any): void {
    this.subscribers.forEach((subscriber) => {
      try {
        switch (eventType) {
          case 'message':
            subscriber.onMessage?.(data);
            break;
          case 'notification':
            subscriber.onNotification?.(data);
            break;
          case 'notificationDeleted':
            subscriber.onNotificationDeleted?.(data);
            break;
          case 'presenceUpdate':
            subscriber.onPresenceUpdate?.(data);
            break;
          case 'connectionChanged':
            subscriber.onConnectionChanged?.(data);
            break;
          case 'conversationUpdate':
            subscriber.onConversationUpdate?.(data);
            break;
          case 'profileUpdate':
            subscriber.onProfileUpdate?.(data);
            break;
        }
      } catch (error) {
        debugLogger.error('RealtimeCentral', 'Error notifying subscriber:', error);
      }
    });
  }

  // SIMPLIFIED cleanup
  cleanup(): void {
    debugLogger.debug('RealtimeCentral', 'Cleaning up all channels and subscriptions');
    
    // Remove all channels
    if (this.notificationChannel) {
      supabase.removeChannel(this.notificationChannel);
      this.notificationChannel = null;
    }
    
    if (this.presenceChannel) {
      supabase.removeChannel(this.presenceChannel);
      this.presenceChannel = null;
    }
    
    if (this.connectionsChannel) {
      supabase.removeChannel(this.connectionsChannel);
      this.connectionsChannel = null;
    }
    
    if (this.conversationsChannel) {
      supabase.removeChannel(this.conversationsChannel);
      this.conversationsChannel = null;
    }
    
    if (this.profileChannel) {
      supabase.removeChannel(this.profileChannel);
      this.profileChannel = null;
    }
    
    // Remove all chat channels
    this.chatChannels.forEach((channel) => {
      supabase.removeChannel(channel);
    });
    this.chatChannels.clear();
    
    // Clear subscribers
    this.subscribers.clear();
    
    // Reset state
    this.isActivated = false;
    this.currentUserId = null;
  }

  // FIXED: Get current presence state in the format expected by useSimplePresence
  getPresenceState(): any {
    try {
      const presenceState: any = {};
      
      debugLogger.debug('RealtimeCentral', 'Getting presence state...');
      
      // Get presence from main presence channel
      if (this.presenceChannel) {
        try {
          const mainPresence = this.presenceChannel.presenceState();
          debugLogger.debug('RealtimeCentral', 'Main presence raw data:', mainPresence);
          
          if (mainPresence && typeof mainPresence === 'object') {
            // Convert to expected format: array of PresenceUser objects
            const convertedMainPresence: PresenceUser[] = [];
            
            for (const [key, presenceArray] of Object.entries(mainPresence)) {
              if (Array.isArray(presenceArray)) {
                presenceArray.forEach((presence: any) => {
                  if (presence && typeof presence === 'object') {
                    convertedMainPresence.push({
                      userId: presence.userId || presence.user_id || key,
                      status: presence.status || 'online',
                      lastUpdate: presence.timestamp || presence.lastUpdate || Date.now()
                    });
                  }
                });
              }
            }
            
            if (convertedMainPresence.length > 0) {
              presenceState['main_presence'] = convertedMainPresence;
              debugLogger.debug('RealtimeCentral', 'Converted main presence:', convertedMainPresence);
            }
          }
        } catch (error) {
          debugLogger.warn('RealtimeCentral', 'Error getting main presence:', error);
        }
      }
      
      // Get presence from all chat channels
      this.chatChannels.forEach((channel, conversationId) => {
        try {
          const chatPresence = channel.presenceState();
          debugLogger.debug('RealtimeCentral', `Chat presence raw data for ${conversationId}:`, chatPresence);
          
          if (chatPresence && typeof chatPresence === 'object') {
            // Convert to expected format: array of PresenceUser objects
            const convertedChatPresence: PresenceUser[] = [];
            
            for (const [key, presenceArray] of Object.entries(chatPresence)) {
              if (Array.isArray(presenceArray)) {
                presenceArray.forEach((presence: any) => {
                  if (presence && typeof presence === 'object') {
                    convertedChatPresence.push({
                      userId: presence.userId || presence.user_id || key,
                      status: presence.status || 'online',
                      lastUpdate: presence.timestamp || presence.lastUpdate || Date.now()
                    });
                  }
                });
              }
            }
            
            if (convertedChatPresence.length > 0) {
              presenceState[`chat_${conversationId}`] = convertedChatPresence;
              debugLogger.debug('RealtimeCentral', `Converted chat presence for ${conversationId}:`, convertedChatPresence);
            }
          }
        } catch (error) {
          debugLogger.warn('RealtimeCentral', 'Error getting presence from chat channel:', conversationId, error);
        }
      });
      
      debugLogger.debug('RealtimeCentral', 'Final presence state:', presenceState);
      return presenceState;
    } catch (error) {
      debugLogger.error('RealtimeCentral', 'Error getting presence state:', error);
      return {};
    }
  }
}

export const realtimeCentral = RealtimeCentral.getInstance();
